import React, { useCallback } from 'react';
import type { ReactNode } from 'react';
import { Box, Flex, Link } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useCustomerGet } from '../../bff-api/customers/useCustomerGet';
import { isRegistered } from '../../contexts/utils/customer-utils';
import { PageUrl } from '../../pages/utils/page-url';
import { Button, Header, Text } from '../../ui-components';
import { Drawer } from '../../ui-components/drawer';
import { useCustomPreferenceOrDefault, useUrl } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { LetterEmailIcon, PerksNotFilledIcon } from '../icons';
import { SoftLink } from '../soft-link/SoftLink';

interface Props {
    id: string;
    isOpen: boolean;
    onClose: () => void;
}

export const PerksDrawer: React.FC<Props> = ({ id, isOpen, onClose }: Props) => {
    const intl = useIntl();
    const loginShowUrl = useUrl(PageUrl.LOGIN_SHOW);
    const { data: customer } = useCustomerGet();
    const location = useLocation();

    const component = useCallback(
        (chunks: ReactNode[]) => (
            <Text color="slt-primary-green.1000" style="bold">
                {chunks}
            </Text>
        ),
        [],
    );

    const isPwaAccountRedirectEnabled = useCustomPreferenceOrDefault(
        CustomPreference.PWA_ACCOUNT_REDIRECT,
        true,
    );

    if (!customer) {
        return null;
    }

    const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}`);
    const loginUrl = `${
        isPwaAccountRedirectEnabled ? PageUrl.ACCOUNT_LOGIN : loginShowUrl
    }?redirect-url=${redirectUrl}`;
    const registerUrl = `${PageUrl.ACCOUNT_REGISTRATION}?redirect-url=${redirectUrl}`;

    const { firstName } = customer;
    const isReturningCustomer = isRegistered(customer);

    return (
        <Drawer
            id={id}
            title={intl.formatMessage({
                id: 'perks.drawer.title',
                defaultMessage: 'SLT Perks',
            })}
            isOpen={isOpen}
            onClose={onClose}
            isFullPage
        >
            <Flex direction="column" gap={8}>
                {isReturningCustomer ? (
                    <Flex
                        data-testid={`${id}-returning-customer`}
                        direction="column"
                        alignItems="center"
                        gap={3}
                    >
                        <Header as="h3" variant="header3" id={`${id}-first-name`}>
                            <FormattedMessage
                                id="returning-customer-firstName"
                                defaultMessage="Hi, {firstName}!"
                                values={{ firstName }}
                            />
                        </Header>

                        <Flex direction="column" alignItems="center">
                            <Text id={`${id}-earn-perks`}>
                                <FormattedMessage
                                    id="returning-customer-earn-perks"
                                    defaultMessage="Earn Perks with your next purchase."
                                />
                            </Text>

                            <Text id={`${id}-redeem-perks`} align="center">
                                <FormattedMessage
                                    id="returning-customer-redeem-perks"
                                    defaultMessage="Redeem your earned Perks on any product online and/or in-store."
                                />
                            </Text>
                        </Flex>
                    </Flex>
                ) : (
                    <Flex
                        data-testid={`${id}-guest-customer`}
                        direction="column"
                        alignItems="center"
                        gap={3}
                    >
                        <Header as="h3" variant="header3" id={`${id}-ways-to-redeem`}>
                            <FormattedMessage
                                id="guest-customer-ways-to-redeem"
                                defaultMessage="Ways To Redeem"
                            />
                        </Header>

                        <Flex direction="column" alignItems="center" gap={6}>
                            <Text id={`${id}-redeem-your-earned-perks`} align="center">
                                <FormattedMessage
                                    id="guest-customer-redeem-your-earned-perks"
                                    defaultMessage="Redeem your earned Perks on any product Online and/or In-Store"
                                />
                            </Text>

                            <SoftLink data-testid={`${id}-sign-up-link`} to={registerUrl}>
                                <Button id={`${id}-sign-up`} variant="primary-black">
                                    <FormattedMessage
                                        id="guest-customer-sign-up"
                                        defaultMessage="Sign Up"
                                    />
                                </Button>
                            </SoftLink>
                        </Flex>

                        <Flex alignItems="center" gap={1}>
                            <Text id={`${id}-already-a-member`}>
                                <FormattedMessage
                                    id="guest-customer-already-a-member"
                                    defaultMessage="Already a member?"
                                />
                            </Text>

                            <Link
                                data-testid={`${id}-log-in-here-link`}
                                /** When isPwaAccountRedirectEnabled is cleaned, this Link should be a SoftLink (for consistency) */
                                {...(isPwaAccountRedirectEnabled
                                    ? { as: ReactRouterLink, to: loginUrl }
                                    : { href: loginUrl })}
                                _hover={{ textDecoration: 'none' }}
                            >
                                <Text id={`${id}-log-in-here`} type="link" style="bold">
                                    <FormattedMessage
                                        id="guest-customer-log-in-here"
                                        defaultMessage="Log in here"
                                    />
                                </Text>
                            </Link>
                        </Flex>
                    </Flex>
                )}

                <Box borderBottomWidth={1} borderColor="slt-pepper.300" />

                <Flex direction="column" gap={4}>
                    <Text id={`${id}-how-it-works`} textTransform="uppercase" style="bold">
                        <FormattedMessage id="how-it-works" defaultMessage="How it works" />
                    </Text>

                    <Flex direction="column" gap={6}>
                        <Flex alignItems="center" gap={4}>
                            <Flex
                                backgroundColor="slt-beige.400"
                                borderRadius={100}
                                width={20}
                                height={20}
                                flexShrink={0}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <PerksNotFilledIcon data-testid={`${id}-perks-not-filled-icon`} />
                            </Flex>

                            <Flex direction="column" gap={1}>
                                <Text id={`${id}-earn-perks-every-3-months`} style="bold">
                                    <FormattedMessage
                                        id="earn-perks-every-3-months"
                                        defaultMessage="Earn Perks Every 3 Months:"
                                    />
                                </Text>

                                <Text id={`${id}-your-perks-3-month-period`}>
                                    <FormattedMessage
                                        id="your-perks-calculated-3-month-period"
                                        defaultMessage="Your Perks are calculated over a 3-month period."
                                    />
                                </Text>
                            </Flex>
                        </Flex>

                        <Flex alignItems="center" gap={4}>
                            <Flex
                                backgroundColor="slt-beige.400"
                                borderRadius={100}
                                width={20}
                                height={20}
                                flexShrink={0}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <LetterEmailIcon data-testid={`${id}-letter-email-icon`} />
                            </Flex>

                            <Flex direction="column" gap={1}>
                                <Text id={`${id}-receive-promo-codes`} style="bold">
                                    <FormattedMessage
                                        id="receive-promo-codes-via-email"
                                        defaultMessage="Receive Promo Codes via Email:"
                                    />
                                </Text>

                                <Text id={`${id}-send-your-perks`}>
                                    <FormattedMessage
                                        id="send-your-perks-your-inbox"
                                        defaultMessage="We'll send your Perks directly to your inbox, so be sure to use the same email at checkout!"
                                    />
                                </Text>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>

                <Flex
                    direction="column"
                    backgroundColor="slt-beige.400"
                    borderRadius={16}
                    padding={4}
                >
                    <Text id={`${id}-reward-levels`} textTransform="uppercase" style="bold">
                        <FormattedMessage id="reward-levels" defaultMessage="Reward levels" />
                    </Text>

                    <Flex direction="column" marginTop={4} gap={6}>
                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-up-to-50`} style="bold">
                                <FormattedMessage
                                    id="spend-up-to-50"
                                    defaultMessage="Spend up to $50"
                                />
                            </Text>

                            <Text id={`${id}-get-5-off`}>
                                <FormattedMessage
                                    id="get-5-off"
                                    defaultMessage="Get <component>$5</component> off with a $30 purchase"
                                    values={{ component }}
                                />
                            </Text>
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-up-to-100`} style="bold">
                                <FormattedMessage
                                    id="spend-up-to-100"
                                    defaultMessage="Spend up to $100"
                                />
                            </Text>

                            <Text id={`${id}-get-10-off`}>
                                <FormattedMessage
                                    id="get-10-off"
                                    defaultMessage="Get <component>$10</component> off with a $40 purchase"
                                    values={{ component }}
                                />
                            </Text>
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-up-to-150`} style="bold">
                                <FormattedMessage
                                    id="spend-up-to-150"
                                    defaultMessage="Spend up to $150"
                                />
                            </Text>

                            <Text id={`${id}-get-15-off`}>
                                <FormattedMessage
                                    id="get-15-off"
                                    defaultMessage="Get <component>$15</component> off with a $60 purchase"
                                    values={{ component }}
                                />
                            </Text>
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-up-to-250`} style="bold">
                                <FormattedMessage
                                    id="spend-up-to-250"
                                    defaultMessage="Spend up to $250"
                                />
                            </Text>

                            <Text id={`${id}-get-20-off`}>
                                <FormattedMessage
                                    id="get-20-off"
                                    defaultMessage="Get <component>$20</component> off with a $80 purchase"
                                    values={{ component }}
                                />
                            </Text>
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-250-more`} style="bold">
                                <FormattedMessage
                                    id="spend-250-more"
                                    defaultMessage="Spend $250 or more"
                                />
                            </Text>

                            <Text id={`${id}-get-25-off`}>
                                <FormattedMessage
                                    id="get-25-off"
                                    defaultMessage="Get <component>$25</component> off with a $100 purchase"
                                    values={{ component }}
                                />
                            </Text>
                        </Flex>
                    </Flex>
                </Flex>

                <SoftLink data-testid={`${id}-learn-more-link`} to={PageUrl.PERKS}>
                    <Text id={`${id}-learn-more-message`} type="link" style="bold">
                        <FormattedMessage
                            id="learn-more-about-slt-perks"
                            defaultMessage="Learn more about SLT Perks"
                        />
                    </Text>
                </SoftLink>
            </Flex>
        </Drawer>
    );
};
